import * as React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { Box3, Vector3 } from 'three';

import cameraManager from '../../../../../../../../js/viewer/camera-manager';
import { Cluster } from '../../../../../../../../js/viewer/elements/cluster';
import { useForwardPointerEvents } from '../../../../../../../hooks/use-forward-pointer-event';
import { defaultTheme } from '../../../../../../../themes/default/styled-component/theme';

type Props = {
  cluster: Cluster;
};

/**
 * POI Label with bottom arrow (chat-bubble style) pointing to the POI.
 */
export const ClusterLabel: React.FC<Props> = (props) => {
  const imageUrl = props.cluster.getThumbnailResourceUrl();

  const forwardRef = useForwardPointerEvents<HTMLDivElement>(['mouseup', 'pointerup', 'click']);

  const focusOnClusterMembers = () => {
    const members = props.cluster.members;

    const focusBox = new Box3().setFromPoints(members.map((poi) => poi.position));
    const boxSize = focusBox.getSize(new Vector3()).length();
    focusBox.expandByScalar(boxSize * 0.1);
    cameraManager().getActiveController()?.focusOnBox(focusBox);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <div ref={forwardRef}>
        {imageUrl ? (
          <Bubble onClick={focusOnClusterMembers}>
            <Thumbnail alt="POI thumbnail" src={imageUrl} />
            <CountBadge $count={props.cluster.getClusterSize()}>
              {props.cluster.getClusterSize()}
            </CountBadge>
          </Bubble>
        ) : (
          <NoThumbnailClusterBadge
            $count={props.cluster.getClusterSize()}
            onClick={focusOnClusterMembers}
          >
            {props.cluster.getClusterSize()}
          </NoThumbnailClusterBadge>
        )}
      </div>
    </ThemeProvider>
  );
};

const Bubble = styled.div`
  position: relative;
  width: 3em;
  height: 3em;
  transform: translate(0, -50%);

  pointer-events: auto;
  cursor: pointer;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  /* clip corners only on image */
  border: 2px solid ${(props) => props.theme.color.brand.dark};
  border-radius: 100%;
  display: block;
`;

type BadgeProps = { $count: number };
export const CountBadge = styled.span.withConfig<BadgeProps>({
  shouldForwardProp: (prop) => prop !== '$count',
})`
  position: absolute;
  right: 0;
  bottom: 0;

  background-color: ${(props) => props.theme.color.brand.dark};
  color: ${(props) => props.theme.color.gray.darkest};
  text-align: center;
  border-radius: 100%;
  pointer-events: none;

  font-weight: 600;

  /* small shadow for contrast on very bright images */
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.35);

  /* size scales with digit count (2-digit, 3-digit, 4-digit) */
  ${({ $count: count }) => {
    const digits = String(count).length;
    const size = digits === 1 ? 1.25 : digits === 2 ? 1.4 : digits === 3 ? 1.7 : 2.0;
    const lineHeight = digits === 1 ? '' : digits === 2 ? '1.3' : digits === 3 ? '1.9' : '2.3';
    return css`
      min-width: ${size}rem;
      min-height: ${size}rem;
      line-height: ${lineHeight};
      padding: 0.1rem;
    `;
  }}
  font-size: 0.9rem;
`;

export const NoThumbnailClusterBadge = styled.span<BadgeProps>`
  /* positioning helpers – keep if you need vertical alignment in your overlay */
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  pointer-events: auto;
  cursor: inherit;

  font-weight: 600;
  color: ${({ theme }) => theme.color.gray.darkest};

  /* diameter & font-size scale with the number of digits */
  ${({ $count: count }) => {
    const digits = String(count).length;
    const diameter = digits === 1 ? 2.0 : digits === 2 ? 2.4 : digits === 3 ? 2.8 : 3.2;
    return css`
      width: ${diameter}rem;
      height: ${diameter}rem;
    `;
  }}
  font-size: 0.9rem;

  position: relative;
  z-index: 0;

  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;

    background: ${({ theme }) => theme.color.brand.dark}bb;
    border: 3px solid ${({ theme }) => theme.color.brand.dark};

    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    z-index: -1; /* stays behind the numeral */
  }
`;
